import { Box, styled, Tooltip } from '@material-ui/core';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { SButton, SSearchInput, SText } from '@setvi/shared/components';
import { CheckIcon, HomeIcon } from '@setvi/shared/components/sicons';
import { TextColor } from '@setvi/shared/enums';
import CloseIcon from '@material-ui/icons/Close';
import { distances } from '@setvi/shared/styles';
import { Categories } from 'componentsV2/categiries';
import { useEffect, useState } from 'react';
import { Breadcrumb, Category } from 'Services/MongoRealm/Types';
import { HomeIconContainer } from '../styled';

const StyledSearch = styled(SSearchInput)({
  padding: `${distances.px.small} ${distances.px.base}`,
  backgroundColor: '#F8F9FA',
  borderRadius: 120,
  marginBottom: distances.px.base,

  '& input': {
    '&::placeholder': {
      color: `${TextColor.SlateGrey} !important`,
      opacity: '1 !important'
    }
  },

  '& fieldset': {
    border: '1px solid #EEF0F2 !important'
  }
});

const StyledButton = styled(SButton)({
  width: 'fit-content',
  borderRadius: 120,
  fontSize: 14,
  height: 32,
  minHeight: 'unset',
  padding: distances.px.small
});

const iconStyle = {
  height: 22,
  width: 22
};

export interface CategoriesContentProps {
  onSelectCategory: (categoryId: Category['_id']) => void;
  selectedCategory: Category;
}

export const CategoriesContent = ({
  selectedCategory,
  onSelectCategory
}: CategoriesContentProps) => {
  const [search, setSearch] = useState<string>('');
  const [category, setCategory] = useState(selectedCategory);
  const [expandedCategories, setExpandedCategories] = useState<Breadcrumb[]>(
    []
  );

  const toggleCategoryExpansion = (currCat: Category) => {
    const isExpanded = expandedCategories.find(c => c._id === currCat._id);

    if (isExpanded) {
      setExpandedCategories(prev => prev.filter(c => c._id !== category._id));
    } else {
      // If i click on category i want to expand it and collapse all other categories except the breacumb
      setExpandedCategories(currCat.breadcrumbs);
    }
  };

  const handleSelectCategory = (cat: Category) => {
    setCategory(cat);

    if (cat.children.length) {
      toggleCategoryExpansion(cat);
    }
  };

  useEffect(() => {
    setCategory(selectedCategory);
    setExpandedCategories(selectedCategory?.breadcrumbs || []);
  }, [selectedCategory]);

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      mt={distances.px.base}
      overflow="hidden">
      <StyledSearch
        searchedValue={search}
        onChange={val => setSearch(val)}
        searchIconSize="14px"
      />
      {(selectedCategory || category) && (
        <Box
          display="flex"
          alignItems="center"
          mb={distances.px.base}
          justifyContent={selectedCategory ? 'space-between' : 'end'}>
          {selectedCategory && (
            <StyledButton
              variant="text"
              startIcon={<CloseIcon style={{ fontSize: 14 }} />}
              onClick={() => {
                onSelectCategory(null);
              }}>
              Clear
            </StyledButton>
          )}
          {category && (
            <StyledButton
              variant="outlined"
              startIcon={
                <CheckIcon
                  fill={category && TextColor.Blue}
                  style={{ fontSize: 10 }}
                />
              }
              onClick={() => {
                // eslint-disable-next-line no-underscore-dangle
                onSelectCategory(category._id);
              }}>
              Done
            </StyledButton>
          )}
        </Box>
      )}
      <Box
        overflow="hidden auto"
        flex={1}
        display="flex"
        flexDirection="column">
        <Box
          height={32}
          display="flex"
          alignItems="center"
          justifyContent="space-between">
          <Box display="flex" alignItems="center" gridGap="4px">
            <Tooltip title="Return to all category" arrow>
              <HomeIconContainer>
                <HomeIcon
                  onClick={() => {
                    if (selectedCategory) return onSelectCategory(null);
                    setExpandedCategories([]);
                    setCategory(null);
                    setSearch('');
                  }}
                />
              </HomeIconContainer>
            </Tooltip>
            <SText
              title="All Categories"
              fontColor={TextColor.Main}
              size="sm"
              lineHeight="20px"
            />
          </Box>
        </Box>
        <Box flex={1} mt={1}>
          <Categories
            treeViewProps={{
              defaultCollapseIcon: <ExpandMoreRoundedIcon style={iconStyle} />,
              defaultExpandIcon: <ChevronRightRoundedIcon style={iconStyle} />
            }}
            onSelectCategory={handleSelectCategory}
            expandedCategories={expandedCategories}
            selectedCategory={category}
            search={search}
          />
        </Box>
      </Box>
    </Box>
  );
};
